<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <span v-if="isUpdateForm">{{ title || $t('user.update_title') }}</span>
        <span v-else>{{ title || $t('user.create_title') }}</span>
        <template v-if="isUpdateForm">
          <b-badge :variant="entity.is_active ? 'success' : 'secondary'" class="ml-3">
            {{ $t( entity.is_active ? 'global.active' : 'global.inactive') }}
          </b-badge>
          <b-button type="submit" size="sm" class="pull-right"
            :variant="entity.is_active ? 'danger' : 'success'" :disabled="spinners.isChangingActive"
            @click="changeUserActive(!entity.is_active )">
            <b-spinner v-if="spinners.isChangingActive" type="grow" small/>
            <i v-if="entity.is_active" class="fa fa-ban"/>
            <i v-else class="fa fa-check"/>
            {{ entity.is_active ? $t('user.deactivate') : $t('user.activate') }}
          </b-button>
          <b-button 
            size="sm" 
            variant="warning" 
            class="pull-right mr-2" 
            :disabled="!canActivateTrial"
            @click="activateTrial"
          >
            Activate Trial
          </b-button>
        </template>
        <slot name="inheader"></slot>
      </div>
      <div v-if="spinners.contentIsLoading" class="text-center">
        <b-spinner type="grow" variant="primary"/>
      </div>
      <div v-else>
        <b-form-row autocomplete="off">
          <b-col cols="12" md="4">
            <b-form-group :label="$t('user.name')" :invalid-feedback="fErr('name')" :disabled="isDisabled">
              <b-form-input type="text" :placeholder="$t('user.name_placeholder')" autocomplete="off"
                :class="{ 'is-invalid' : fErr('name') }" v-model="entity.name"/>
            </b-form-group>
            <b-form-group :label="$t('user.surname')" :invalid-feedback="fErr('surname')" :disabled="isDisabled">
              <b-form-input type="text" :placeholder="$t('user.surname_placeholder')" autocomplete="off"
                :class="{ 'is-invalid' : fErr('surname') }" v-model="entity.surname"/>
            </b-form-group>
            <b-form-group :label="$t('user.email')" :invalid-feedback="fErr('email')" :disabled="isDisabled">
              <b-form-input type="email" :placeholder="$t('user.email')" autocomplete="off"
                :class="{ 'is-invalid' : fErr('email') }" v-model="entity.email"/>
            </b-form-group>
            <template v-if="!isUpdateForm">
              <b-form-group :label="$t('user.password')" :invalid-feedback="fErr('password')">
                <b-form-input :type="seePasswords ? 'text' : 'password'" autocomplete="new-password"
                  :placeholder="$t('user.password')"
                  :class="{ 'is-invalid' : fErr('password') }" v-model="entity.password"
                />
              </b-form-group>
              <b-form-group :label="$t('user.password_confirmation')" :invalid-feedback="fErr('password_confirmation')">
                <b-form-input :type="seePasswords ? 'text' : 'password'" autocomplete="off"
                  :placeholder="$t('user.password_confirmation') "
                  :class="{ 'is-invalid' : fErr('password_confirmation') }" v-model="entity.password_confirmation"
                />
              </b-form-group>
            </template>
          </b-col>
          <b-col cols="12" md="8">
            <b-form-group :label="$t('user.roles')">
              <treeselect :class="{ 'treeselect-error' : fErr('roles') }"
                :placeholder="$t('user.roles_placeholder')" autocomplete="off"
                :noOptionsText="$t('global.no_options_available')" :noResultsText="$t('global.no_results_found')"
                :multiple="true" :options="roleOptions" v-model="entity.roles"/>
              <span class="error-text" v-if="fErr('roles')">{{ fErr('roles') }}</span>
            </b-form-group>
            <b-form-group :label="$dt('user.phone', 'Phone')" :invalid-feedback="fErr('phone')" :disabled="isDisabled">
              <b-form-input type="tel" :placeholder="$dt('user.phone', 'Phone')" autocomplete="off"
                :class="{ 'is-invalid' : fErr('phone') }" v-model="entity.phone"/>
            </b-form-group>
            <b-form-group :label="$dt('user.marketing_segment', 'Marketing Segment')">
              <b-form-input :value="entity.marketing_segment" disabled />
            </b-form-group>
            <b-form-checkbox v-if="!isUpdateForm" id="is_active" v-model="entity.is_active" :value="true">
              {{ $t('global.active') }}
            </b-form-checkbox>
          </b-col>
          <b-col v-if="isUpdateForm" cols="12" md="4">
            <b-button v-if="!changePasswordCard" variant="info" size="sm" @click="changePasswordCard = true">
              <i class="icon-lock"/><span class="ml-2">{{ $t('user.change_password') }}</span>
            </b-button>
            <ChangePassword v-else :oldPassword="false" :userId="id" @close="changePasswordCard = false"/>
          </b-col>
        </b-form-row>
      </div>
      <div v-if="!isEmbedded" slot="footer">
        <div class="pull-right">
          <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click="save()">
            <b-spinner v-if="spinners.isSaving" type="grow" small/>
            {{ $t('global.save') }}
          </b-button>
          <router-link :to="{ name: 'Users' }">
            <b-button type="reset" variant="info" class="ml-2">{{ $t('global.to_list') }}</b-button>
          </router-link>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import merge from 'lodash.merge';
import Services from '@/services/index';
import Treeselect from '@riophae/vue-treeselect';
import ChangePassword from '@/views/Administration/Users/ChangePassword.vue';

export default {
  mounted() {
    if (!this.isEmbedded) this.load();
  },
  props: {
    // If id === null, form will be considered create form else update
    id: { type: [Number, String], default: null },
    // if title == false to hide header, pass string to override default title
    title: { type: [String, Boolean], default: undefined },
    // if disabled === true, form disabled, can be enabled and edited.
    disabled: { type: [Boolean], default: false },
    // pass errors from parent form if embedded
    errors: { type: Object, default() { return {}; } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          name: '',
          surname: '',
          email: '',
          password: '',
          password_confirmation: '',
          is_active: false,
          roles: [],
        };
      },
    },
  },
  data() {
    return {
      spinners: {
        contentIsLoading: false,
        isChangingActive: false,
        isSaving: false,
      },
      subscriptionStatus: null,
      activateTrialLoading: false,
      seePasswords: false,
      changePasswordCard: false,
      entity: this.value,
      roleOptions: [],
      errorMsg: this.errors,
    };
  },
  components: { Treeselect, ChangePassword },
  computed: {
    // embedded form will have value passed using v-model, we could also make a custom prop for checking this
    isEmbedded() { return this.$options.propsData.value !== undefined; },
    isUpdateForm() { return this.id !== null; },
    isDisabled() { return this.disabled; },
    canActivateTrial() {
      return this.isUpdateForm 
        && this.subscriptionStatus 
        && !['trialing', 'active'].includes(this.subscriptionStatus) 
        && this.activateTrialLoading === false;
    }
  },
  watch: {
    errors() { this.errorMsg = this.errors; },
  },
  methods: {
    getForUpdate: Services.users.editData,
    getForCreate: Services.users.createData,
    pushUpdate: Services.users.update,
    pushCreate: Services.users.create,
    updateActive: Services.users.updateActive,
    load() {
      this.spinners.contentIsLoading = true;
      const action = this.isUpdateForm ? this.getForUpdate : this.getForCreate;
      action(this.$route.params.id).then((res) => {
        this.spinners.contentIsLoading = false;
        this.entity = this.isUpdateForm ? res.data.user : this.$options.props.value.default();
        if (this.isUpdateForm) this.setSelected();
        this.subscriptionStatus = this.isUpdateForm ? res.data.subscription_status : null;
        this.roleOptions = res.data.roles.map((item) => {
          return {
            id: item['id'],
            label: item['name']
          };
        });
      }).catch(({ errors, message }) => {
        this.spinners.contentIsLoading = false;
        this.alertError({ text: message });
      });
    },
    save() {
      if (this.isEmbedded) return;
      this.spinners.isSaving = true;
      const action = this.isUpdateForm ? this.pushUpdate : this.pushCreate;
      action(this.entity).then((res) => {
        this.spinners.isSaving = false;
        this.alertSuccess();
        merge(this.entity, res.data); // This will not delete keys but will preserve original object
        this.setSelected();
        this.errorMsg = {};
        if (!this.isUpdateForm) {
          this.$router.push({ name: 'UserUpdate', params: { id: res.data.id } });
        }
      }).catch(({ errors, message }) => {
        this.spinners.isSaving = false;
        this.errorMsg = errors;
        this.alertError({ text: message });
      });
    },
    setSelected() {
      this.entity.roles = this.entity.roles.map((item) => { return item.id; });
    },
    changeUserActive(isActive) {
      this.spinners.isChangingActive = true;
      this.updateActive(this.$route.params.id, isActive).then((res) => {
        this.spinners.isChangingActive = false;
        this.alertSuccess();
        this.load();
      }).catch(({ errors, message }) => {
        this.spinners.isChangingActive = false;
        this.alertError({ text: message });
      });
    },
    activateTrial() {
      this.activateTrialLoading = true;
      Services.users.startTrial(this.entity.id).then(() => {
        this.alertSuccess();
      }).catch((error) => {
        console.error(error);
        const e = error.data.data
        this.alertError({ text: e })
      });
    },
  }
};
</script>

<style scoped>
</style>
